// マスタCSVインポートのポップアップ。
// ファイルを受け取る → サーバーで試す → 結果を見て決める → 取り込む、という段階を持つ。
// 書式と列の点検はブラウザ側、参照の整合とIDの決定はサーバー側が受け持つ。
// この画面は段階の移り変わりと、サーバーへの依頼の呼び出しだけを持つ。
// 利用者へ見せる文言は masterCsvDialogState に置き、ここからは定数か関数として参照する。
import { useRef, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from "@mui/material";
import type {
  MasterImportApproval, MasterImportCollision, MasterImportError, MasterImportIdPolicy, MasterImportRename,
  MasterImportRequester,
} from "../../../schema/console/master_import";
import { errorMessageOf } from "../../../util/typed";
import type { ApplyOutcome } from "./csv/masterCsvDialogState";
import {
  MASTER_IMPORT_APPLYING_MESSAGE, MASTER_IMPORT_APPLY_STAGE_LABEL, MASTER_IMPORT_APPROVAL_CHANGED_NOTICE,
  MASTER_IMPORT_CHECKING_MESSAGE, MASTER_IMPORT_CHECK_STAGE_LABEL, MASTER_IMPORT_COLLISION_NOTICE,
  MASTER_IMPORT_FILE_ERROR_NOTICE, MASTER_IMPORT_ID_RENUMBER_NOTICE, MASTER_IMPORT_OVERWRITE_NOTICE,
  MASTER_IMPORT_READING_FILES_MESSAGE,
  MASTER_IMPORT_READY_NOTICE, MASTER_IMPORT_RENUMBER_CHECKING_MESSAGE, MASTER_IMPORT_RENUMBER_CHECK_STAGE_LABEL,
  MASTER_IMPORT_SERVER_ERROR_NOTICE, MASTER_IMPORT_TABLE_ROW_NOTICE,
  applyOutcomeOfResponse, applyRequestOf, approvalOf, checkOutcomeOf, collisionRowsOf, errorViewOfFileErrors,
  errorViewOfServerErrors, failureSeverityOf, importFailureTextOf, importRequestOf, importTargetRowsOf,
  importedButRefreshFailedTextOf, renumberCheckOutcomeOf,
} from "./csv/masterCsvDialogState";
import { intakeOfFiles } from "./csv/masterCsvFileIntake";
import type { MasterCsvFileError, MasterCsvTable } from "./csv/masterCsvFiles";
import { tablesOfSourceFiles } from "./csv/masterCsvFiles";
import {
  MasterCsvCollisionList, MasterCsvDropArea, MasterCsvErrorTable, MasterCsvImportTargetList, MasterCsvPlanReview,
} from "./MasterCsvImportPanels";

// 取り込む内容を確かめてもらう段階が持つ中身。承認前の確認と、承認が食い違ったあとのやり直しで
// 同じ一覧を出すため、どちらの段階も同じ内容を持ち回る。
// nameStamp は、名前の重複を避けるためにサーバーが名前の末尾へ付ける取り込み時刻。
// 試行の応答で受け取った値を段階ごとに持ち回り、本送信でそのまま送り返す。
// 送り返さないとサーバーが時刻を取り直し、確認画面に出した名前と実際に書き込まれる名前がずれる。
type ImportPlanReviewPayload = {
  collisions: MasterImportCollision[];
  idPolicy: MasterImportIdPolicy;
  nameStamp: string;
  renames: MasterImportRename[];
};

// ポップアップが今どの段階にいるか。段階ごとに見せる内容と押せるボタンが変わる。
// failed の isImportFinished は、サーバー側の取り込みが済んだあとでつまずいたかどうかを表す。
// 済んでいる場合に送り直しを促すと同じデータを二重に取り込むため、見せ方を分ける必要がある。
// approvalChanged は、承認した内容と書き込む直前の内容が食い違って中止された段階。
type ImportStage =
  | { name: "select" }
  | { name: "busy"; message: string }
  | { name: "fileError"; errors: MasterCsvFileError[]; hiddenErrorCount: number }
  | { name: "serverError"; errors: MasterImportError[] }
  | { name: "ready"; nameStamp: string }
  | { name: "collision"; collisions: MasterImportCollision[]; nameStamp: string; renames: MasterImportRename[] }
  | (ImportPlanReviewPayload & { name: "renamePreview" })
  | (ImportPlanReviewPayload & { name: "approvalChanged" })
  | { name: "failed"; isImportFinished: boolean; message: string };

export type MasterCsvImportDialogProps = {
  open: boolean;
  onClose: () => void;
  // 取り込みの依頼先。実際の通信の代わりに検証用の関数を渡せるようにしてある。
  importMasters: MasterImportRequester;
  // 取り込みが確定したあとの後始末。マスタの取り直しと完了の知らせを呼び出し側が行う。
  onImported: (message: string) => void | Promise<void>;
};

export default function MasterCsvImportDialog({ open, onClose, importMasters, onImported }: MasterCsvImportDialogProps) {
  const [stage, setStage] = useState<ImportStage>({ name: "select" });
  const [tables, setTables] = useState<MasterCsvTable[]>([]);
  // ボタンを押した直後はまだ描画が切り替わっていないため、連打を段階だけでは防げない。
  // 走っている処理があるかどうかを描画と切り離して覚えておく。
  const isRunningRef = useRef<boolean>(false);

  const isBusy = stage.name === "busy";

  const backToSelect = () => {
    setTables([]);
    setStage({ name: "select" });
  };

  const closeDialog = () => {
    backToSelect();
    onClose();
  };

  // 背景のクリックと Esc キーでの閉じる操作。処理中は受け付けない。
  // 閉じても通信は止まらず、あとから届いた結果が段階を書き換えてしまうため。
  const onRequestClose = () => {
    if (!isBusy) {
      closeDialog();
    }
  };

  // ボタンから始まる処理を1つずつに限る。連打で同じ依頼が2回走るのを防ぐ。
  const runExclusively = async (task: () => Promise<void>) => {
    if (!isRunningRef.current) {
      isRunningRef.current = true;
      try {
        await task();
      } finally {
        isRunningRef.current = false;
      }
    }
  };

  // 試行送信。サーバーの検証結果から次の段階を決める。
  // 名前が変わる行があれば、IDが重なっていなくても確認画面を挟む。
  const checkWithServer = async (targetTables: readonly MasterCsvTable[]) => {
    setStage({ name: "busy", message: MASTER_IMPORT_CHECKING_MESSAGE });
    try {
      const response = await importMasters(importRequestOf(targetTables, "dryRun", "keepId"));
      const nameStamp = response.nameStamp;
      const outcome = checkOutcomeOf(response);
      if (outcome.name === "blocked") {
        setStage({ name: "serverError", errors: outcome.errors });
        return;
      }
      if (outcome.name === "idCollision") {
        setStage({ name: "collision", collisions: outcome.collisions, nameStamp, renames: outcome.renames });
        return;
      }
      if (outcome.name === "renamePreview") {
        setStage({ name: "renamePreview", collisions: [], idPolicy: "keepId", nameStamp, renames: outcome.renames });
        return;
      }
      setStage({ name: "ready", nameStamp });
    } catch (error) {
      setStage({ name: "failed", isImportFinished: false, message: importFailureTextOf(error, MASTER_IMPORT_CHECK_STAGE_LABEL) });
    }
  };

  const onFilesSelected = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    setStage({ name: "busy", message: MASTER_IMPORT_READING_FILES_MESSAGE });
    const intake = await intakeOfFiles(files);
    if (intake.errors.length > 0) {
      setStage({ name: "fileError", errors: intake.errors, hiddenErrorCount: 0 });
      return;
    }
    const parsed = tablesOfSourceFiles(intake.sourceFiles);
    if (!parsed.ok) {
      setStage({ name: "fileError", errors: parsed.errors, hiddenErrorCount: parsed.hiddenErrorCount });
      return;
    }
    setTables(parsed.tables);
    await checkWithServer(parsed.tables);
  };

  // 本送信をサーバーへ依頼する。返る内容はサーバー側の取り込みが確定したかどうかだけを表す。
  // 確認画面に出した名前をそのまま書き込ませるため、試行で受け取った取り込み時刻を添える。
  // 承認した内容も添え、書き込む直前の内容と食い違えばサーバーが何も書き込まずに知らせてくれる。
  const sendApply = async (idPolicy: MasterImportIdPolicy, nameStamp: string, approval: MasterImportApproval): Promise<ApplyOutcome> => {
    try {
      const response = await importMasters(applyRequestOf(tables, idPolicy, nameStamp, approval));
      return applyOutcomeOfResponse(response);
    } catch (error) {
      return { kind: "failed", message: importFailureTextOf(error, MASTER_IMPORT_APPLY_STAGE_LABEL) };
    }
  };

  // 本送信と、そのあとの画面の作り直し。
  // サーバーが取り込みを確定させたあとで画面の作り直しにつまずいても、取り込みは取り消されない。
  // 「失敗しました」と伝えると同じファイルを送り直させることになるため、伝える内容を分ける。
  const applyImport = async (idPolicy: MasterImportIdPolicy, nameStamp: string, approval: MasterImportApproval) => {
    setStage({ name: "busy", message: MASTER_IMPORT_APPLYING_MESSAGE });
    const outcome = await sendApply(idPolicy, nameStamp, approval);
    // 送った取り込み時刻が古くて棄却されると、サーバーは新しい時刻で計画を組み直す。
    // 見せ直す名前は組み直した時刻で作られているため、次に送り返す時刻も応答の側へそろえる。
    // 送った時刻を持ち回ると、承認するたびに同じ食い違いが返り続けて取り込めなくなる。
    if (outcome.kind === "approvalMismatch") {
      setStage({ name: "approvalChanged", collisions: outcome.collisions, idPolicy, nameStamp: outcome.nameStamp, renames: outcome.renames });
      return;
    }
    if (outcome.kind === "serverError") {
      setStage({ name: "serverError", errors: outcome.errors });
      return;
    }
    if (outcome.kind === "failed") {
      setStage({ name: "failed", isImportFinished: false, message: outcome.message });
      return;
    }
    try {
      await onImported(outcome.message);
      closeDialog();
    } catch (error) {
      const message = importedButRefreshFailedTextOf(errorMessageOf(error));
      setStage({ name: "failed", isImportFinished: true, message });
    }
  };

  // 「IDをそのまま使用して上書き」を選んだとき。
  // 名前が変わる予定は試行の時点で分かっているため、問い合わせ直さずそのまま確認画面へ進む。
  const onChooseKeepId = async (collisions: MasterImportCollision[], renames: MasterImportRename[], nameStamp: string) => {
    if (renames.length > 0) {
      setStage({ name: "renamePreview", collisions, idPolicy: "keepId", nameStamp, renames });
      return;
    }
    await applyImport("keepId", nameStamp, approvalOf(collisions, [], "keepId"));
  };

  // 「新しく採番し直す」を選んだとき。名前が変わる予定の行があれば、取り込む前に見てもらう。
  // 画面に出す内容も承認する内容も、この採番し直しの試行の応答だけから取る。
  // 前の試行の値を混ぜると、利用者が見ていない内容を承認したことになってしまう。
  const onChooseRenumber = async () => {
    setStage({ name: "busy", message: MASTER_IMPORT_RENUMBER_CHECKING_MESSAGE });
    try {
      const response = await importMasters(importRequestOf(tables, "dryRun", "renumber"));
      const nameStamp = response.nameStamp;
      const outcome = renumberCheckOutcomeOf(response);
      if (outcome.name === "blocked") {
        setStage({ name: "serverError", errors: outcome.errors });
        return;
      }
      if (outcome.name === "renamePreview") {
        setStage({ name: "renamePreview", collisions: outcome.collisions, idPolicy: "renumber", nameStamp, renames: outcome.renames });
        return;
      }
      await applyImport("renumber", nameStamp, approvalOf(outcome.collisions, [], "renumber"));
    } catch (error) {
      setStage({ name: "failed", isImportFinished: false, message: importFailureTextOf(error, MASTER_IMPORT_RENUMBER_CHECK_STAGE_LABEL) });
    }
  };

  function stageContent() {
    if (stage.name === "busy") {
      return (
        <Stack direction="row" alignItems="center" gap={1.5} py={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">{stage.message}</Typography>
        </Stack>
      );
    }
    if (stage.name === "fileError") {
      return (
        <Stack gap={1}>
          <Alert severity="error">{MASTER_IMPORT_FILE_ERROR_NOTICE}</Alert>
          <MasterCsvErrorTable view={errorViewOfFileErrors(stage.errors, stage.hiddenErrorCount)} />
        </Stack>
      );
    }
    if (stage.name === "serverError") {
      return (
        <Stack gap={1}>
          <Alert severity="error">{MASTER_IMPORT_SERVER_ERROR_NOTICE}</Alert>
          <MasterCsvErrorTable view={errorViewOfServerErrors(stage.errors, tables)} />
        </Stack>
      );
    }
    if (stage.name === "ready") {
      return (
        <Stack gap={1}>
          <Alert severity="success">{MASTER_IMPORT_READY_NOTICE}</Alert>
          <MasterCsvImportTargetList rows={importTargetRowsOf(tables, [], "keepId")} />
        </Stack>
      );
    }
    if (stage.name === "collision") {
      return (
        <Stack gap={1}>
          <Alert severity="warning">{MASTER_IMPORT_COLLISION_NOTICE}</Alert>
          <MasterCsvCollisionList rows={collisionRowsOf(stage.collisions, tables)} />
          <Typography variant="body2" color="warning.main">{MASTER_IMPORT_OVERWRITE_NOTICE}</Typography>
        </Stack>
      );
    }
    if (stage.name === "renamePreview") {
      return (
        <Stack gap={1}>
          <MasterCsvPlanReview tables={tables} collisions={stage.collisions} idPolicy={stage.idPolicy} renames={stage.renames} />
        </Stack>
      );
    }
    if (stage.name === "approvalChanged") {
      return (
        <Stack gap={1}>
          <Alert severity="warning">{MASTER_IMPORT_APPROVAL_CHANGED_NOTICE}</Alert>
          <MasterCsvPlanReview tables={tables} collisions={stage.collisions} idPolicy={stage.idPolicy} renames={stage.renames} />
        </Stack>
      );
    }
    if (stage.name === "failed") {
      return <Alert severity={failureSeverityOf(stage.isImportFinished)}>{stage.message}</Alert>;
    }
    return <MasterCsvDropArea onFilesSelected={(files) => runExclusively(() => onFilesSelected(files))} />;
  }

  function stageActions() {
    // 処理中は閉じる手立てを出さない。閉じても通信は止まらず、結果だけがあとから書き戻るため。
    if (stage.name === "busy") {
      return null;
    }
    if (stage.name === "ready") {
      const nameStamp = stage.nameStamp;
      return (
        <>
          <Button onClick={closeDialog}>キャンセル</Button>
          <Button onClick={backToSelect}>ファイルを選び直す</Button>
          <Button variant="contained" onClick={() => runExclusively(() => applyImport("keepId", nameStamp, approvalOf([], [], "keepId")))}>取り込む</Button>
        </>
      );
    }
    if (stage.name === "collision") {
      const collisions = stage.collisions;
      const renames = stage.renames;
      const nameStamp = stage.nameStamp;
      return (
        <>
          <Button onClick={closeDialog}>キャンセル</Button>
          <Button onClick={backToSelect}>ファイルを選び直す</Button>
          <Button color="warning" onClick={() => runExclusively(() => onChooseKeepId(collisions, renames, nameStamp))}>IDをそのまま使用して上書き</Button>
          <Button variant="contained" onClick={() => runExclusively(() => onChooseRenumber())}>新しく採番し直す</Button>
        </>
      );
    }
    // 承認した内容と食い違って中止された場合も、見せ直した内容をそのまま承認して送り直せるようにする。
    if (stage.name === "renamePreview" || stage.name === "approvalChanged") {
      const collisions = stage.collisions;
      const idPolicy = stage.idPolicy;
      const nameStamp = stage.nameStamp;
      const renames = stage.renames;
      return (
        <>
          <Button onClick={closeDialog}>キャンセル</Button>
          <Button onClick={backToSelect}>ファイルを選び直す</Button>
          <Button variant="contained" onClick={() => runExclusively(() => applyImport(idPolicy, nameStamp, approvalOf(collisions, renames, idPolicy)))}>この内容で取り込む</Button>
        </>
      );
    }
    // 取り込みが確定したあとの不具合では、送り直すと二重に取り込まれるため選び直させない。
    if (stage.name === "failed" && stage.isImportFinished) {
      return <Button onClick={closeDialog}>閉じる</Button>;
    }
    if (stage.name === "fileError" || stage.name === "serverError" || stage.name === "failed") {
      return (
        <>
          <Button onClick={closeDialog}>閉じる</Button>
          <Button variant="contained" onClick={backToSelect}>ファイルを選び直す</Button>
        </>
      );
    }
    return <Button onClick={closeDialog}>閉じる</Button>;
  }

  return (
    <Dialog open={open} onClose={onRequestClose} disableEscapeKeyDown={isBusy} fullWidth maxWidth="md">
      <DialogTitle>CSV インポート</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <Typography variant="body2">{MASTER_IMPORT_TABLE_ROW_NOTICE}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>{MASTER_IMPORT_ID_RENUMBER_NOTICE}</Typography>
        </Alert>
        {stageContent()}
      </DialogContent>
      <DialogActions>{stageActions()}</DialogActions>
    </Dialog>
  );
}
